import { CheckCircle2, Clock3, History, SkipForward, XCircle } from "lucide-react";

const statusMeta = {
    Completed: { icon: CheckCircle2, dot: "bg-emerald-500", badge: "bg-emerald-50 text-emerald-700" },
    Pending: { icon: Clock3, dot: "bg-blue-500", badge: "bg-blue-50 text-blue-700" },
    Skipped: { icon: SkipForward, dot: "bg-amber-500", badge: "bg-amber-50 text-amber-700" },
    Failed: { icon: XCircle, dot: "bg-rose-500", badge: "bg-rose-50 text-rose-700" },
};

function formatDate(value) {
    if (!value) return "Date unavailable";

    return new Date(value).toLocaleString(undefined, {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
    });
}

export default function WorkflowTimeline({ history = [] }) {
    return (
        <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="flex items-center gap-3 border-b border-slate-100 px-5 py-4">
                <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-slate-100 text-slate-600"><History className="h-4 w-4" /></span>
                <div>
                    <h3 className="font-semibold text-slate-900">Journey history</h3>
                    <p className="text-xs text-slate-500">Completed steps, notes, and ownership changes.</p>
                </div>
                {history.length > 0 && <span className="ml-auto rounded-full bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-600">{history.length}</span>}
            </div>

            <div className="p-4 sm:p-5">
                {history.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center">
                        <History className="mx-auto h-6 w-6 text-slate-300" />
                        <p className="mt-2 text-sm font-semibold text-slate-700">No journey history yet</p>
                        <p className="mt-0.5 text-xs text-slate-500">Completed workflow steps will appear here.</p>
                    </div>
                ) : (
                    <div>
                        {history.map((item, index) => {
                            const meta = statusMeta[item.status] || statusMeta.Pending;
                            const StatusIcon = meta.icon;
                            const showStage = index === 0 || history[index - 1]?.node?.stage?.id !== item.node?.stage?.id;

                            return (
                                <div key={item.id} className="relative flex gap-3 pb-3 last:pb-0">
                                    {index < history.length - 1 && <div className="absolute bottom-0 left-[15px] top-8 w-px bg-slate-200" />}
                                    <span className={`relative z-10 mt-1 h-3 w-3 shrink-0 rounded-full ring-4 ring-white ${meta.dot}`} />

                                    <div className="min-w-0 flex-1 rounded-xl border border-slate-200 bg-white px-4 py-3">
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="min-w-0">
                                                {showStage && <p className="mb-1 text-[10px] font-bold uppercase tracking-[0.12em] text-blue-600">{item.node?.stage?.name || "Unknown stage"}</p>}
                                                <h4 className="truncate text-sm font-semibold text-slate-900">{item.node?.name || "Workflow activity"}</h4>
                                                <p className="mt-1 text-xs text-slate-500">
                                                    {item.changed_by?.name || "System"} · {formatDate(item.completed_at || item.created_at)}
                                                </p>
                                            </div>
                                            <span className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-bold ${meta.badge}`}>
                                                <StatusIcon className="h-3 w-3" /> {item.status}
                                            </span>
                                        </div>
                                        {item.notes && <p className="mt-2 rounded-lg bg-slate-50 px-3 py-2 text-xs leading-relaxed text-slate-600">{item.notes}</p>}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </section>
    );
}
